import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeOff, User as UserIcon, Briefcase } from "lucide-react";
import { apiClient } from "@/lib/api";
import { useAuthStore } from "@/stores/authStore";
import { useGoogleLogin } from "@react-oauth/google";
import logo from '../assets/images/base.png';
import artieLogo from '../assets/images/artie.png';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { TurnstileWidget } from "@/components/TurnstileWidget";

const signupSchema = z.object({
  name: z.string().min(2, "Full name must be at least 2 characters"),
  email: z.string().email("Invalid email address"),
  password: z.string().min(8, "Password must be at least 8 characters"),
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

type SignupFormValues = z.infer<typeof signupSchema>;

export default function SignupPage() {
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [userType, setUserType] = useState<"creator" | "brand">("creator");
  const navigate = useNavigate();
  const { login, checkAuth } = useAuthStore();
  const [searchParams] = useSearchParams();
  const referralToken = searchParams.get('ref');
  const referralCode = searchParams.get('referralCode');
  const emailFromInvite = searchParams.get('email');
  const redirectTo = searchParams.get('redirectTo');

  // Bot check. The site key comes from the API at runtime: no key, no widget, no token sent.
  // With a key, the button waits for a token, and a failed submit resets the widget because
  // Turnstile tokens are single-use.
  const [turnstileSiteKey, setTurnstileSiteKey] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileReset, setTurnstileReset] = useState(0);
  const honeypot = useRef<HTMLInputElement>(null);
  useEffect(() => {
    apiClient.getAuthConfig()
      .then((c) => setTurnstileSiteKey(c.turnstile_site_key || null))
      .catch(() => setTurnstileSiteKey(null));
  }, []);
  const botFields = () => ({
    turnstile_token: turnstileToken || undefined,
    website: honeypot.current?.value || undefined,
  });

  const form = useForm<SignupFormValues>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      name: "",
      email: emailFromInvite || "",
      password: "",
      confirmPassword: "",
    },
  });

  const googleSignup = useGoogleLogin({
    // Switching to implicit flow to bypass account chooser and consent screen issues on signup/login
    // Implicit flow allows better control over the 'prompt' parameter
    // flow: 'implicit' is default
    prompt: 'select_account', // Force account selection (as requested) but avoid consent screen
    onSuccess: async (tokenResponse) => {
      try {
        // Map frontend user type to backend role
        const role = userType === 'brand' ? 'brand_user' : 'user';
        // Pass access_token instead of code
        const response = await apiClient.googleLogin(undefined, referralCode || undefined, tokenResponse.access_token, role);
        
        // Safari fallback: if response contains access_token, store it for Safari BEFORE calling login/checkAuth
        const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
        if (isSafari && response.access_token) {
          console.log("Safari detected: Using token fallback for authentication");
          // Store token temporarily for Safari BEFORE any auth checks
          sessionStorage.setItem('safari_auth_token', response.access_token);
        }
        
        login(); // Update local state
        
        // For Safari, add a small delay to ensure token is available before checkAuth
        if (isSafari && response.access_token) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
        
        const user = await checkAuth(); // Verify session via cookie

        // Track conversion if coming from a share link
        if (referralToken) {
          try {
            await apiClient.trackShareConversion(referralToken);
          } catch (error) {
            console.error("Failed to track conversion:", error);
          }
        }

        toast.success("Welcome!");

        if (redirectTo) {
          navigate(redirectTo);
        } else if (user?.role === 'admin') {
          navigate("/admin");
        } else if (user?.role === 'brand_user') {
           // Redirect brand users to brand onboarding if needed
           // Ideally check onboarding status, but for now specific brand onboarding or dashboard
           navigate("/brand-onboarding/company-info");
        } else {
          navigate("/onboarding-v2");
        }
      } catch (error) {
        console.error("Google Signup Failed", error);
        
        // Safari-specific error handling
        const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
        if (isSafari && error instanceof Error) {
          if (error.message.includes("Authentication failed") || error.message.includes("cookies")) {
            toast.error("Safari detected: Please ensure cookies are enabled and try again. You may need to disable 'Prevent Cross-Site Tracking' in Safari settings.");
          } else {
            toast.error("Google signup failed. If you're using Safari, please check your privacy settings.");
          }
        } else {
          toast.error("Google signup failed");
        }
      }
    },
    onError: () => {
      console.log('Login Failed');
      toast.error("Google login failed");
    },
  });

  async function onSubmit(data: SignupFormValues) {
    setIsLoading(true);
    try {
      if (userType === 'brand') {
        // Brand Signup Flow
        const signupResponse = await apiClient.brandSignup({
          name: data.name,
          email: data.email,
          password: data.password,
          ...botFields(),
        });

        // Store the token and authenticate the user
        if (signupResponse.access_token) {
           sessionStorage.setItem('safari_auth_token', signupResponse.access_token);
        }

        // Call checkAuth to update the auth store with the new user
        await checkAuth();

        toast.success("Brand account created! Welcome.");
        
        // Redirect directly to brand onboarding company info
        navigate("/brand-onboarding/company-info");

      } else {
        // Creator Signup Flow (Existing)
        await apiClient.signup({
            name: data.name,
            email: data.email,
            password: data.password,
            referral_code: referralCode || undefined,
            ...botFields(),
          });
    
          // Auto-login after signup
          const response = await apiClient.login({
            email: data.email,
            password: data.password,
          });
  
      // Safari fallback: if response contains access_token, store it for Safari BEFORE calling login/checkAuth
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
      if (isSafari && response.access_token) {
        console.log("Safari detected: Using token fallback for authentication");
        // Store token temporarily for Safari BEFORE any auth checks
        sessionStorage.setItem('safari_auth_token', response.access_token);
      }

        login(); // Update local state in auth store
        
      // For Safari, add a small delay to ensure token is available before checkAuth
      if (isSafari && response.access_token) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
        const user = await checkAuth(); // Verify session
    
          // Track conversion if coming from a share link
          if (referralToken) {
            try {
              await apiClient.trackShareConversion(referralToken);
            } catch (error) {
              console.error("Failed to track conversion:", error);
            }
          }
    
          toast.success("Account created! Welcome.");
    
          if (redirectTo) {
            navigate(redirectTo);
          } else if (user?.role === 'admin') {
            navigate("/admin");
          } else {
            navigate("/onboarding-v2");
          }
        }
    } catch (error) {
      console.error(error);
      setTurnstileReset((n) => n + 1); // the token, if any, is spent
      
      // Safari-specific error handling
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
      if (error instanceof Error && error.message.includes("Verification failed")) {
        // The bot check refused or the token expired; this must win over the Safari cookie advice.
        toast.error(error.message);
      } else if (isSafari && error instanceof Error) {
        if (error.message.includes("Authentication failed") || error.message.includes("cookies")) {
          toast.error("Safari detected: Please ensure cookies are enabled and try again. You may need to disable 'Prevent Cross-Site Tracking' in Safari settings.");
        } else {
          toast.error("Account creation failed. If you're using Safari, please check your privacy settings.");
        }
      } else {
        toast.error(error instanceof Error ? error.message : "Failed to create account");
      }
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className="antialiased bg-white text-neutral-900 h-screen flex flex-col lg:flex-row overflow-hidden font-geist">

      {/* Left Panel: Form Section */}
      <div className="lg:w-1/2 flex flex-col lg:p-12 z-10 overflow-y-auto bg-white w-full h-full pt-6 pr-6 pb-6 pl-6 relative justify-between">


        {/* Header / Logo */}
        <header className="flex justify-center lg:justify-start relative z-10">
          <Link to="/" className="flex items-center gap-2 group transition-opacity hover:opacity-80">
            <img src={artieLogo} alt="Artie" className="w-8 h-8" />
            <span className="text-xl font-bold tracking-tight text-neutral-900">Artie</span>
          </Link>
        </header>

        {/* Main Form Content */}
        <main className="flex-1 flex flex-col justify-center max-w-[440px] w-full mx-auto py-12 lg:py-0 relative z-10">

          <div className="text-center lg:text-left mb-6">
            <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-neutral-900 mb-3">Create your account</h1>
            <p className="text-neutral-500 text-base leading-relaxed">
              Start your journey with Artie. Understand your reach, brand fit, and production readiness.
            </p>
          </div>

          {/* User Type Toggle */}
          <div className="mb-6">
            <Tabs
              defaultValue="creator"
              value={userType}
              onValueChange={(v) => setUserType(v as "creator" | "brand")}
              className="w-full"
            >
              <TabsList className="grid w-full grid-cols-2 h-12 bg-neutral-100 p-1 rounded-lg">
                <TabsTrigger
                  value="creator"
                  className="flex items-center justify-center gap-2 text-sm font-medium data-[state=active]:bg-white data-[state=active]:text-neutral-900 data-[state=active]:shadow-sm rounded-md h-full transition-all"
                >
                  <UserIcon className="w-4 h-4" />
                  <span>Creator</span>
                </TabsTrigger>
                <TabsTrigger
                  value="brand"
                  className="flex items-center justify-center gap-2 text-sm font-medium data-[state=active]:bg-white data-[state=active]:text-neutral-900 data-[state=active]:shadow-sm rounded-md h-full transition-all"
                >
                  <Briefcase className="w-4 h-4" />
                  <span>Brand</span>
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">

              {/* Name Input */}
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem className="space-y-1.5">
                    <FormLabel className="block text-sm font-medium text-neutral-700">Full Name</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          {...field}
                          type="text"
                          placeholder="Your full name"
                          className="block w-full h-12 px-4 rounded-lg border border-neutral-200 bg-white/80 backdrop-blur-sm text-neutral-900 placeholder:text-neutral-400 focus:border-neutral-900 focus:ring-1 focus:ring-neutral-900 focus:outline-none transition-all duration-200 shadow-sm sm:text-sm"
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem className="space-y-1.5">
                    <FormLabel className="block text-sm font-medium text-neutral-700">Email address</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          {...field}
                          type="email"
                          placeholder={userType === 'brand' ? "brand@company.com" : "you@example.com"}
                          className="block w-full h-12 px-4 rounded-lg border border-neutral-200 bg-white/80 backdrop-blur-sm text-neutral-900 placeholder:text-neutral-400 focus:border-neutral-900 focus:ring-1 focus:ring-neutral-900 focus:outline-none transition-all duration-200 shadow-sm sm:text-sm"
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Password Input */}
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem className="space-y-1.5">
                    <FormLabel className="block text-sm font-medium text-neutral-700">Password</FormLabel>
                    <FormControl>
                      <div className="relative group">
                        <Input
                          {...field}
                          type={showPassword ? "text" : "password"}
                          placeholder="Min. 8 characters"
                          className="block w-full h-12 px-4 rounded-lg border border-neutral-200 bg-white/80 backdrop-blur-sm text-neutral-900 placeholder:text-neutral-400 focus:border-neutral-900 focus:ring-1 focus:ring-neutral-900 focus:outline-none transition-all duration-200 shadow-sm sm:text-sm pr-10"
                        />
                        <button
                          type="button"
                          className="absolute inset-y-0 right-0 px-3 flex items-center text-neutral-400 hover:text-neutral-600 transition-colors focus:outline-none"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? (
                            <EyeOff className="w-5 h-5" />
                          ) : (
                            <Eye className="w-5 h-5" />
                          )}
                        </button>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Confirm Password Input */}
              <FormField
                control={form.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem className="space-y-1.5">
                    <FormLabel className="block text-sm font-medium text-neutral-700">Confirm Password</FormLabel>
                    <FormControl>
                      <div className="relative group">
                        <Input
                          {...field}
                          type={showPassword ? "text" : "password"}
                          placeholder="Re-enter your password"
                          className="block w-full h-12 px-4 rounded-lg border border-neutral-200 bg-white/80 backdrop-blur-sm text-neutral-900 placeholder:text-neutral-400 focus:border-neutral-900 focus:ring-1 focus:ring-neutral-900 focus:outline-none transition-all duration-200 shadow-sm sm:text-sm pr-10"
                        />
                        <button
                          type="button"
                          className="absolute inset-y-0 right-0 px-3 flex items-center text-neutral-400 hover:text-neutral-600 transition-colors focus:outline-none"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? (
                            <EyeOff className="w-5 h-5" />
                          ) : (
                            <Eye className="w-5 h-5" />
                          )}
                        </button>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Honeypot: a person never sees or fills this; a form-filling bot does. */}
              <input
                ref={honeypot}
                type="text"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                className="absolute -left-[10000px] top-auto w-px h-px overflow-hidden"
              />

              {turnstileSiteKey && (
                <TurnstileWidget siteKey={turnstileSiteKey} onToken={setTurnstileToken} resetKey={turnstileReset} />
              )}

              {/* Primary CTA */}
              <button
                type="submit"
                disabled={isLoading || (!!turnstileSiteKey && !turnstileToken)}
                className="w-full flex items-center justify-center h-12 px-6 rounded-lg text-white font-medium bg-neutral-900 hover:bg-neutral-800 active:scale-[0.98] transition-all duration-200 shadow-sm hover:shadow focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-neutral-900 mt-2 disabled:opacity-70"
              >
                {isLoading ? "Creating account..." : `Sign Up as ${userType === 'brand' ? 'Brand' : 'Creator'}`}
              </button>

            </form>
          </Form>

          {/* Divider - For both Creator and Brand */}
          <div className="relative my-8">
            <div className="absolute inset-0 flex items-center" aria-hidden="true">
              <div className="w-full border-t border-neutral-200"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-3 bg-white text-neutral-500">Or continue with</span>
            </div>
          </div>

          {/* Google OAuth */}
          <button
            type="button"
            onClick={() => googleSignup()}
            className="w-full flex items-center justify-center h-12 px-6 rounded-lg bg-white/80 backdrop-blur-sm border border-neutral-200 text-neutral-700 font-medium hover:bg-neutral-50 active:bg-neutral-100 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-neutral-200 group"
          >
            <svg className="h-5 w-5 mr-3 group-hover:scale-110 transition-transform duration-200" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"></path>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"></path>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"></path>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"></path>
            </svg>
            Continue with Google
          </button>

          {/* Footer Link */}
          <div className="mt-8 text-center">
            <p className="text-sm text-neutral-600">
              Already have an account?{" "}
              <Link to="/login" className="font-semibold text-neutral-900 hover:text-neutral-700 hover:underline transition-colors">Log in</Link>
            </p>
          </div>

        </main>

        <div className="hidden lg:block h-8 relative z-10"></div>
      </div>

      {/* Right Panel: Artie Mascot & Score */}
      <div className="hidden lg:flex lg:w-1/2 overflow-hidden bg-white border-neutral-200 border-l relative items-center justify-center">

        {/* Background Elements */}
        <div className="absolute inset-0 pointer-events-none">
          {/* Subtle gradient mesh */}
          <div className="absolute top-[10%] right-[10%] w-[500px] h-[500px] bg-orange-100/40 rounded-full blur-[100px] mix-blend-multiply"></div>
          <div className="absolute bottom-[10%] left-[10%] w-[500px] h-[500px] bg-yellow-100/40 rounded-full blur-[100px] mix-blend-multiply"></div>
        </div>

        {/* Main Content Container */}
        <div className="flex flex-col w-full max-w-lg z-10 mr-auto ml-auto relative items-center justify-center">

          {/* Artie Illustration Composition */}
          <div className="relative w-full h-96 mb-10 flex items-center justify-center">
            {/* New Artie (Dog Image) */}
            <div className="w-96 h-96 relative shrink-0 z-10">
              <img src={logo} alt="Artie" className="w-full h-full object-contain relative z-10" />
            </div>
          </div>

          {/* Text Content */}
          <div className="text-center max-w-sm px-6">
            <h2 className="text-2xl font-bold tracking-tight text-neutral-900 mb-3">Lets Get it Made</h2>
            <p className="text-neutral-500 text-base leading-relaxed mb-8">
              Develop, package and finance your show, vertical short or film
            </p>
          </div>

        </div>
      </div>
    </div>
  );
}
